<template>
  <!--
    AB#21697 — set or clear the customer's in-hand date.

    One date, taken from what the customer said, saved without ceremony: no
    reason code and no customer e-mail, because it is their date rather than
    our promise (that is the estimated shipping date's job). Any status — the
    customer can tell you their date before production starts. Saving writes
    a timeline entry and puts the date into ELSA's read straight away.
  -->
  <Drawer form
    title="In-hand date"
    :subtitle="`Order #${orderNo} · when the customer needs it`"
    width="440px"
    data-test="in-hand-drawer"
    @close="emit('close')"
  >
    <div class="ih-field">
      <label class="ih-label" for="in-hand-date">Date the customer needs the goods in hand</label>
      <Datepicker
        id="in-hand-date"
        v-model="date"
        format="dd/MM/yyyy"
        placeholder="Choose a date"
        data-test="in-hand-date"
      />
      <p class="ih-help">
        Comes from the customer's own words (email, ticket, call). Saving writes a timeline entry
        and puts the date into ELSA's read straight away. Clear it if the customer withdraws the
        date.
      </p>
      <p v-if="error" class="ih-error" role="status" data-test="in-hand-error">{{ error }}</p>
    </div>

    <template #footer>
      <button
        v-if="initial"
        type="button"
        class="dash-btn ih-clear"
        data-test="in-hand-clear"
        :disabled="saving"
        @click="save(null)"
      >
        Clear date
      </button>
      <button type="button" class="dash-btn" data-test="in-hand-cancel" @click="emit('close')">
        Cancel
      </button>
      <button
        type="button"
        class="dash-btn dash-btn--primary"
        data-test="in-hand-save"
        :disabled="saving || !date || !changed"
        @click="save(toYmd(date))"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Datepicker from '@/components/Shared/Datepicker.vue';
import Drawer from '@/components/Shared/Drawer.vue';
import { showToast } from '@/composables/swal';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { toYmd } from '@/utils/inHandDate';

const props = defineProps<{
  orderItemId: number;
  orderNo: string | number;
  /** The saved in-hand date (Y-m-d), or null. */
  initial: string | null;
  /**
   * AB#21732 — a date to start from when none is saved: the one ELSA found in
   * the order's notes. Still saved only when the person presses Save.
   */
  suggested?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  /** Saved (or cleared): the new value, Y-m-d or null. */
  (e: 'saved', value: string | null): void;
}>();

const fromYmd = (v: string | null): Date | null =>
  v ? new Date(v.slice(0, 10) + 'T00:00:00') : null;

const date = ref<Date | null>(fromYmd(props.initial ?? props.suggested ?? null));
const saving = ref(false);
const error = ref<string | null>(null);

const changed = computed(() => toYmd(date.value) !== (props.initial ?? null));

const save = async (value: string | null) => {
  saving.value = true;
  error.value = null;
  try {
    const { data } = await http.post(`customer-orders/${props.orderItemId}/in-hand-date`, {
      in_hand_date: value,
    });
    const saved = (data?.in_hand_date ?? value) as string | null;
    showToast(saved === null ? 'In-hand date cleared.' : 'In-hand date saved.', 'success');
    emit('saved', saved);
  } catch (e) {
    handleResponseException(e);
    error.value = 'The in-hand date could not be saved. Try again.';
  } finally {
    saving.value = false;
  }
};
</script>

<style lang="scss" scoped>
.ih-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ih-label {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink);
}
.ih-help {
  margin: 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--ink-mute);
}
.ih-error {
  margin: 0;
  font-size: 12px;
  color: var(--danger);
}
.ih-clear {
  margin-right: auto;
  border-color: transparent;
  color: var(--ink-mute);
}
</style>
